"use client";

import { useState } from "react";
import Link from "next/link";
import { register } from "@/lib/auth-api";
import { useUser } from "@/lib/user-context";

export default function RegisterPage() {
  const { refresh } = useUser();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [name, setName] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (password !== passwordConfirm) {
      setError("비밀번호가 일치하지 않습니다");
      return;
    }
    if (!agreeTerms) {
      setError("이용약관에 동의해주세요");
      return;
    }

    setLoading(true);
    try {
      await register(email, password, passwordConfirm, name, agreeTerms, agreeMarketing);
      await refresh();
      window.location.href = "/wiki";
    } catch (err) {
      setError(err instanceof Error ? err.message : "회원가입 실패");
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-sm px-5 py-12">
      <h1 className="mb-2 text-center text-3xl font-extrabold tracking-tight">회원가입</h1>
      <p className="text-muted-foreground mb-8 text-center text-sm">
        이미 계정이 있으신가요?{" "}
        <Link href="/signin" className="font-semibold text-violet-600">
          로그인
        </Link>
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label
            htmlFor="signup-email"
            className="text-muted-foreground mb-1.5 block text-xs font-semibold"
          >
            이메일
          </label>
          <input
            id="signup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@example.com"
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-violet-600"
          />
        </div>

        <div>
          <label
            htmlFor="signup-name"
            className="text-muted-foreground mb-1.5 block text-xs font-semibold"
          >
            이름
          </label>
          <input
            id="signup-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoComplete="name"
            placeholder="홍길동"
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-violet-600"
          />
        </div>

        <div>
          <label
            htmlFor="signup-password"
            className="text-muted-foreground mb-1.5 block text-xs font-semibold"
          >
            비밀번호
          </label>
          <input
            id="signup-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            minLength={8}
            placeholder="최소 8자 이상"
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-violet-600"
          />
        </div>

        <div>
          <label
            htmlFor="signup-password-confirm"
            className="text-muted-foreground mb-1.5 block text-xs font-semibold"
          >
            비밀번호 확인
          </label>
          <input
            id="signup-password-confirm"
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            required
            autoComplete="new-password"
            minLength={8}
            placeholder="비밀번호를 다시 입력해주세요"
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-violet-600"
          />
        </div>

        {/* 약관 동의 */}
        <div className="space-y-2 pt-2">
          <label className="flex cursor-pointer items-start gap-2.5">
            <input
              type="checkbox"
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-violet-600 accent-violet-600"
            />
            <span className="text-sm text-gray-700">
              <span className="text-red-500">[필수]</span>{" "}
              <Link href="/terms" className="underline">
                이용약관
              </Link>{" "}
              및{" "}
              <Link href="/privacy" className="underline">
                개인정보처리방침
              </Link>
              에 동의합니다
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2.5">
            <input
              type="checkbox"
              checked={agreeMarketing}
              onChange={(e) => setAgreeMarketing(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-violet-600 accent-violet-600"
            />
            <span className="text-sm text-gray-500">[선택] 마케팅 정보 수신에 동의합니다</span>
          </label>
        </div>

        {error && (
          <div role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="mt-2 rounded-full bg-violet-600 py-3.5 text-base font-bold text-white shadow-lg shadow-violet-600/15 transition-all hover:-translate-y-0.5 hover:bg-violet-700 disabled:opacity-50"
        >
          {loading ? "가입 중..." : "회원가입"}
        </button>
      </form>
    </div>
  );
}
